'use client';

import type { TextInputProps } from '../text-input';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { forwardRef } from 'react';

import { Icon } from '../../icons';
import { Typography } from '../../typography';

export type FileInputProps = TextInputProps & {
  accept: string;
  isUploading?: boolean;
  successLabel?: string;
  onDelete: () => void;
  url?: string;
};

export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(
  (
    { accept, isUploading, onDelete, successLabel, url, value, ...props },
    ref
  ) => {
    if (value) {
      return (
        <div className="space-y-2">
          <div className="relative flex items-center justify-between rounded-md border border-input bg-background px-3 py-2">
            <div className="flex items-center gap-2.5">
              {!!value && !isUploading && <Icon name="file" />}
              <Typography variant="helper">{value}</Typography>
            </div>

            <div className="flex items-center gap-2.5">
              {!!url && !isUploading && (
                <Icon
                  className="cursor-pointer stroke-primary"
                  name="eye"
                  onClick={() => window.open(url)}
                />
              )}
              {!!value && !isUploading && (
                <Icon
                  className="cursor-pointer"
                  data-testid={`delete-file-${props.name}`}
                  name="trash-2"
                  onClick={onDelete}
                />
              )}
            </div>
            {!!isUploading && (
              <Icon
                className="absolute right-0 top-0 m-2.5 size-5 animate-spin text-muted-foreground"
                name="loader-circle"
              />
            )}
          </div>
          {!!successLabel && !isUploading && (
            <div className="flex items-center gap-1">
              <Icon className="stroke-success" name="circle-check-big" />
              <Typography className="text-success" variant="helper">
                {successLabel}
              </Typography>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className={cn('relative')}>
        <input
          className={cn(
            'block w-full cursor-pointer rounded-lg border border-input text-sm shadow-sm transition',
            'file:me-4 file:cursor-pointer file:border-0 file:border-r file:border-gray-200/20 file:bg-sidebar file:px-4 file:py-2 file:disabled:cursor-not-allowed file:disabled:text-muted-foreground',
            'focus:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground',
            { 'bg-muted': isUploading }
          )}
          id="file-input"
          name="file-input"
          type="file"
          {...props}
          ref={ref}
          accept={accept}
          data-testid={props['data-testid'] ?? props.name}
          disabled={isUploading || props.disabled}
        />
        {!!isUploading && (
          <Icon
            className="absolute right-0 top-0 m-2.5 size-5 animate-spin text-muted-foreground"
            name="loader-circle"
          />
        )}
      </div>
    );
  }
);

FileInput.displayName = 'FileInput';
